import React, { createContext, useContext, useState, useEffect } from 'react';

export interface UserPermissionSet {
  pedido: boolean;
  produto: boolean;
  categoria: boolean;
  status: boolean;
  usuario: boolean;
  setor: boolean;
  configuracao: boolean;
  relatorios: boolean;
}

export interface SectorPermission {
  view: UserPermissionSet;
  add: UserPermissionSet;
  edit: UserPermissionSet;
  delete: UserPermissionSet;
  export: UserPermissionSet;
}

export interface UserProfile {
  id?: string;
  name: string;
  role: string;
  email: string;
  permissions?: SectorPermission;
}

interface AppContextType {
  currentUser: UserProfile | null;
  activeOperator: UserProfile;
  login: (email: string, name?: string, role?: string, permissions?: any, token?: string) => void;
  logout: () => void;
  changeOperator: (role: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  // Helper to generate default permissions based on role
  const generateDefaultPermissions = (roleName: string): SectorPermission => {
    const isAdm = (roleName === 'ADM' || roleName === 'Administração' || roleName === 'Administrador');
    if (isAdm) {
      const allTrue = {
        pedido: true, produto: true, categoria: true, status: true,
        usuario: true, setor: true, configuracao: true, relatorios: true
      };
      return {
        view: allTrue,
        add: allTrue,
        edit: allTrue,
        delete: allTrue,
        export: allTrue
      };
    } else {
      // Non-admin roles (e.g., Corte, Dobra, Solda, Finishing, etc.)
      const allFalse = {
        pedido: false, produto: false, categoria: false, status: false,
        usuario: false, setor: false, configuracao: false, relatorios: false
      };
      return {
        view: { ...allFalse, pedido: true, produto: true, status: true },
        add: allFalse,
        edit: { ...allFalse, pedido: true, status: true },
        delete: allFalse,
        export: allFalse
      };
    }
  };

  // Read initial states from localStorage for durable user experience
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(() => {
    const saved = localStorage.getItem('mc_current_user');
    if (!saved) return null;
    try {
      const parsed = JSON.parse(saved) as UserProfile;
      if (parsed && !parsed.permissions) {
        parsed.permissions = generateDefaultPermissions(parsed.role);
      }
      return parsed;
    } catch {
      return null;
    }
  });

  const activeOperator: UserProfile = currentUser || { 
    name: 'Não Conectado', 
    role: 'VISITANTE', 
    email: '' 
  };

  const login = (email: string, name = 'Usuário', role = 'OPERADOR', permissions?: any, token?: string) => {
    const profilePerms = permissions || generateDefaultPermissions(role);
    const profile = { email, name, role, permissions: profilePerms };
    localStorage.setItem('mc_current_user', JSON.stringify(profile));
    if (token) {
      localStorage.setItem('token', token);
    }
    setCurrentUser(profile);
    setTimeout(() => {
      window.location.reload();
    }, 50);
  };

  const logout = () => {
    localStorage.removeItem('mc_current_user');
    localStorage.removeItem('mc_active_operator');
    localStorage.removeItem('token');
    setCurrentUser(null);
    setTimeout(() => {
      window.location.reload();
    }, 50);
  };

  const changeOperator = (roleName: string) => {
    // Simulação removida. Usuário real logado dita as ações.
  };

  return (
    <AppContext.Provider value={{ currentUser, activeOperator, login, logout, changeOperator }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
